import React from 'react';
import { t } from '../i18n';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text } from './Texte';
import { colors, formes, radius, screenPadding, shadow, spacing, typography } from '../theme/tokens';
import { fleche } from '../i18n/sens';

export interface ScreenHeaderProps {
  // Sans onBack, aucun bouton retour n'est rendu (écrans racine d'onglet).
  onBack?: () => void;
  title?: string;
  // Ligne sous le titre (« 8 CV enregistrés »). Variante carte seulement.
  sousTitre?: string;
  // 'carte' : en-tête des écrans refaits (retour blanc carré, titre plus gras). Sans elle, l'ancien.
  variante?: 'defaut' | 'carte';
  // Élément placé à l'extrémité opposée au retour (menu, bouton Enregistrer).
  droite?: React.ReactNode;
  testID?: string;
}

// En-tête unique réutilisé sur tous les écrans enfants : flèche retour à gauche + titre optionnel.
// La safe area haute est gérée par le `SafeAreaView edges={['top']}` parent ; on ajoute juste un
// padding interne pour ne pas coller le bouton sous l'encoche.
export function ScreenHeader({ onBack, title, sousTitre, variante = 'defaut', droite, testID }: ScreenHeaderProps) {
  const carte = variante === 'carte';
  return (
    <View style={[styles.container, carte && styles.containerCarte]}>
      {onBack ? (
        <Pressable
          testID={testID}
          accessibilityRole="button"
          accessibilityLabel={t('common.back')}
          onPress={onBack}
          hitSlop={8}
          style={carte ? styles.backCarte : styles.backButton}
        >
          <Text style={styles.chevron}>{fleche('←')}</Text>
        </Pressable>
      ) : null}
      {title && carte && sousTitre ? (
        <View style={styles.titres}>
          <Text style={styles.titleCarte} numberOfLines={1}>
            {title}
          </Text>
          <Text testID={testID ? `${testID}-sous-titre` : undefined} style={styles.sousTitre} numberOfLines={1}>
            {sousTitre}
          </Text>
        </View>
      ) : title ? (
        <Text
          style={carte ? styles.titleCarte : styles.title}
          numberOfLines={1}
          // Variante carte : le titre partage la ligne avec une commande à droite (bascule de langue).
          // Sur un écran étroit il se réduit plutôt que d'être coupé.
          adjustsFontSizeToFit={carte}
          minimumFontScale={0.8}
        >
          {title}
        </Text>
      ) : null}
      {droite ?? null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: screenPadding,
    paddingTop: spacing.sm,
    paddingBottom: spacing.sm,
  },
  containerCarte: { gap: spacing.md },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: radius.pill,
    backgroundColor: colors.grey100,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // 44 pt : cible tactile minimale. La maquette dessine un carré aux coins arrondis, pas un rond.
  backCarte: {
    width: formes.cible,
    height: formes.cible,
    borderRadius: formes.rayon.sm,
    backgroundColor: colors.white,
    alignItems: 'center',
    justifyContent: 'center',
    ...shadow.card,
  },
  chevron: {
    fontSize: 22,
    lineHeight: 22,
    color: colors.grey900,
  },
  title: {
    flex: 1,
    fontSize: typography.h3.size,
    fontWeight: typography.h3.weight,
    color: colors.grey900,
  },
  titleCarte: {
    flex: 1,
    fontSize: typography.h2.size,
    lineHeight: typography.h2.lineHeight,
    fontWeight: '800',
    color: colors.grey900,
  },
  // `minWidth: 0` : un titre long se coupe au lieu de pousser le bouton de droite hors de l'écran.
  titres: { flex: 1, minWidth: 0, gap: 1 },
  sousTitre: { fontSize: typography.small.size, lineHeight: typography.small.lineHeight, color: colors.grey500 },
});
